import { useMemo } from 'react';
import {
  InsuranceVerificationDetailsResponse,
  PayerDetail,
} from '@weave/schema-gen-ts/dist/schemas/insurance-verification/v1/insurance_verification_adapter.pb';
import {
  ErrorDetails,
  InsuranceCoverageResponse,
} from '@weave/schema-gen-ts/dist/schemas/insurance-verification/v2/insurance_verification_adapter.pb';
import { isEmpty } from 'lodash-es';
import { InsuranceDetailQueries } from '@frontend/api-insurance-detail';
import { IntegrationsQueries } from '@frontend/api-integrations';
import { DefaultInsuranceDetailsValues } from '../utils/constants';
import type {
  InsuranceDetails,
  UndefinedOrNull,
  UseGetInsuranceDetailsDentalOrNonDentalResponse,
} from '../utils/types';
import { useGetDentalInsuranceDetails } from './use-get-dental-insurance-details';
import { useGetNonDentalInsuranceDetails } from './use-get-non-dental-insurance-details';
import { useGetVerifiedInsuranceDetailsShallowStore } from './use-get-verified-insurance-details-store';

interface GetInsuranceDetailsData {
  isLoading: boolean;
  isRefetching: boolean | undefined;
  insuranceDetails: UndefinedOrNull<InsuranceDetails>;
  hasActiveOrVerifiedInsurance: boolean;
  hasErrors: boolean;
  hasCoverage: boolean;
  isInsuranceDetailsWritebackSupported: boolean;
  requestId?: string;
  insuranceVerificationAPIResData: InsuranceVerificationDetailsResponse | undefined;
  hasInactiveInsuranceDetailsFromPartner?: boolean;
  hasOverrideCoverage?: boolean;
  errorDetails?: ErrorDetails;
  refetchInsuranceDetails?: () => void;
}

const useGetInsuranceDetails = (
  locationId: string,
  personId: string,
  personSourceId: string,
  payersListResponse: PayerDetail[],
  isDental: boolean,
  isWellnessVertical: boolean,
  disableFetchingVerificationData?: boolean
): GetInsuranceDetailsData => {
  const { setVerifiedInsuranceDetails, setInsuranceRequestDetails } = useGetVerifiedInsuranceDetailsShallowStore(
    'setVerifiedInsuranceDetails',
    'setInsuranceRequestDetails'
  );
  const { isInsuranceDetailsWritebackSupported, isLoading: isLoadingWritebackSupportedDetails } =
    IntegrationsQueries.useIsWritebackSupported({
      locationId: locationId!,
      sourceId: personSourceId || '',
    });
  //This API is responsible for getting all the details of insurance by perosn like personId, firstName, lastname
  const { data: insurancePersonDetails, isLoading: isLoadingInsuranceDetailsByPerson } =
    InsuranceDetailQueries.useGetInsuranceDetailsByPersonId({
      personId,
      locationId: locationId || '',
      options: {
        enabled: !!personId && !!locationId && isDental,
      },
    });

  const nonDentalData = useGetNonDentalInsuranceDetails(
    isWellnessVertical,
    personId,
    locationId,
    insurancePersonDetails,
    payersListResponse
  );
  const dentalData = useGetDentalInsuranceDetails(
    isDental,
    personId,
    locationId,
    insurancePersonDetails,
    payersListResponse,
    disableFetchingVerificationData
  );
  const {
    isLoading: isLoadingInsuranceDetails,
    isRefetching,
    insuranceDetails,
    thirdPartyCoverage,
    hasActiveOrVerifiedInsurance,
    insuranceVerificationAPIResData,
    hasInactiveInsuranceDetailsFromPartner,
    refetchInsuranceDetails,
  } = (isDental ? dentalData : nonDentalData) as UseGetInsuranceDetailsDentalOrNonDentalResponse;
  const isLoading =
    isLoadingInsuranceDetails || isLoadingWritebackSupportedDetails || isLoadingInsuranceDetailsByPerson;

  // Setting up the verified insurance details
  const verifiedInsuranceDetails = useMemo<InsuranceDetails>(() => {
    if (!insuranceDetails) {
      setVerifiedInsuranceDetails({ ...DefaultInsuranceDetailsValues, personId: '' });
      return DefaultInsuranceDetailsValues;
    }

    // If the insuranceRequestDetails is not present in the thirdPartyCoverage, we set it to an empty object
    if (thirdPartyCoverage?.insuranceRequestDetails) {
      setInsuranceRequestDetails(thirdPartyCoverage.insuranceRequestDetails);
    } else {
      setInsuranceRequestDetails({});
    }

    setVerifiedInsuranceDetails({ ...insuranceDetails, personId });
    return insuranceDetails;
  }, [insuranceDetails, thirdPartyCoverage, personId, setVerifiedInsuranceDetails, setInsuranceRequestDetails]);

  const hasErrors = !isEmpty((thirdPartyCoverage as InsuranceCoverageResponse)?.errorDetails);
  const hasCoverage = !isEmpty(thirdPartyCoverage?.coverage) && !!thirdPartyCoverage?.coverage.status;
  const hasOverrideCoverage = !isEmpty(insuranceVerificationAPIResData?.coverage?.overriddenCoverage?.coverage);
  const requestId = (thirdPartyCoverage as InsuranceCoverageResponse)?.externalRequestId;
  return {
    isLoading,
    isRefetching,
    refetchInsuranceDetails,
    insuranceDetails: verifiedInsuranceDetails,
    hasActiveOrVerifiedInsurance,
    hasErrors,
    hasCoverage,
    isInsuranceDetailsWritebackSupported,
    requestId,
    insuranceVerificationAPIResData,
    hasOverrideCoverage,
    hasInactiveInsuranceDetailsFromPartner,
    errorDetails: (thirdPartyCoverage as InsuranceCoverageResponse)?.errorDetails,
  };
};

export default useGetInsuranceDetails;
